import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { COFINSRepository } from '@asm/features/sync/repositories/cofins.repository';
import { ICMS1Repository } from '@asm/features/sync/repositories/icms1.repository';
import { ICMS3Repository } from '@asm/features/sync/repositories/icms3.repository';
import { IPI1Repository } from '@asm/features/sync/repositories/ipi1.repository';
import { IPI3Repository } from '@asm/features/sync/repositories/ipi3.repository';
import { PISRepository } from '@asm/features/sync/repositories/pis.repository';
import { ST3Repository } from '@asm/features/sync/repositories/st3.repository';
import {
  COFINSTaxTable,
  ICMS1TaxTable,
  ICMS3TaxTable,
  IPI1TaxTable,
  IPI3TaxTable,
  PISTaxTable,
  ST3TaxTable,
  TaxTableBase,
} from '@asm/features/sync/models/pricing-tables.model';
import { LocalTaxCondition, LocalTaxResult } from '@asm/features/sync/services/pricing/pricing.interfaces';

type DynamicTaxKey = 'ICMS' | 'IPI' | 'ST' | 'PIS' | 'COFINS';
type DynamicFieldName = 'FIELD' | 'FIELD2' | 'FIELD3';

interface DynamicTaxGroup {
  GRUOP: number;
  FIELD: string | null;
  FIELD2: string | null;
  FIELD3: string | null;
  ACTIVIC?: 'X' | null;
  ACTIVIP?: 'X' | null;
  ACTIVST?: 'X' | null;
  ACTIVPI?: 'X' | null;
  ACTIVCO?: 'X' | null;
}

interface TaxContext {
  price: number;
  VALIDFROM: Date;
  VALIDTO: Date;
  COUNTRY: string | null;
  LAND1: string | null;
  SHIPFROM: string | null;
  SHIPTO: string | null;
  STGRP: string | null;
  ICMSTAXPAY: string | null;
  BRSCH: string | null;
  MATNR: string | null;
  NBM: string | null;
  NBMCODE: string | null;
  EXTWG: string | null;
  MTORG: string | null;
  WERKS: string | null;
  MWSKZ: string | null;
  KUNNR: string | null;
}

@Injectable({ providedIn: 'root' })
export class DefaultLocalTaxService {
  private readonly icms1Repo = inject(ICMS1Repository);
  private readonly icms3Repo = inject(ICMS3Repository);
  private readonly ipi1Repo = inject(IPI1Repository);
  private readonly ipi3Repo = inject(IPI3Repository);
  private readonly st3Repo = inject(ST3Repository);
  private readonly pisRepo = inject(PISRepository);
  private readonly cofinsRepo = inject(COFINSRepository);

  private readonly dynamicGroups: DynamicTaxGroup[] = [
    {
      GRUOP: 10,
      FIELD: 'MWSKZ',
      FIELD2: 'MATNR',
      FIELD3: 'WERKS',
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 11,
      FIELD: 'MWSKZ',
      FIELD2: 'NBM',
      FIELD3: 'WERKS',
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 12,
      FIELD: 'MWSKZ',
      FIELD2: 'EXTWG',
      FIELD3: null,
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 13,
      FIELD: 'MWSKZ',
      FIELD2: 'MATNR',
      FIELD3: null,
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 14,
      FIELD: 'MWSKZ',
      FIELD2: 'NBM',
      FIELD3: null,
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 16,
      FIELD: 'MWSKZ',
      FIELD2: 'MTORG',
      FIELD3: null,
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 18,
      FIELD: 'MWSKZ',
      FIELD2: null,
      FIELD3: null,
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 32,
      FIELD: 'BRSCH',
      FIELD2: 'WERKS',
      FIELD3: 'MATNR',
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 34,
      FIELD: 'BRSCH',
      FIELD2: 'WERKS',
      FIELD3: 'NBM',
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 36,
      FIELD: 'BRSCH',
      FIELD2: 'WERKS',
      FIELD3: 'MTORG',
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 40,
      FIELD: 'MTORG',
      FIELD2: null,
      FIELD3: null,
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 42,
      FIELD: 'MATNR',
      FIELD2: 'WERKS',
      FIELD3: null,
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 43,
      FIELD: 'NBM',
      FIELD2: 'WERKS',
      FIELD3: null,
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 44,
      FIELD: 'MATNR',
      FIELD2: null,
      FIELD3: null,
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    { GRUOP: 45, FIELD: 'NBM', FIELD2: 'MATNR', FIELD3: null, ACTIVIC: 'X', ACTIVST: 'X', ACTIVPI: 'X', ACTIVCO: 'X' },
    { GRUOP: 46, FIELD: 'NBM', FIELD2: null, FIELD3: null, ACTIVIC: 'X', ACTIVST: 'X', ACTIVPI: 'X', ACTIVCO: 'X' },
    {
      GRUOP: 48,
      FIELD: 'ICMSTAXPAY',
      FIELD2: null,
      FIELD3: null,
      ACTIVIC: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    {
      GRUOP: 87,
      FIELD: 'KUNNR',
      FIELD2: 'MATNR',
      FIELD3: null,
      ACTIVIC: 'X',
      ACTIVIP: 'X',
      ACTIVST: 'X',
      ACTIVPI: 'X',
      ACTIVCO: 'X',
    },
    { GRUOP: 89, FIELD: null, FIELD2: null, FIELD3: null, ACTIVIC: 'X', ACTIVPI: 'X', ACTIVCO: 'X' },
    { GRUOP: 97, FIELD: null, FIELD2: null, FIELD3: null, ACTIVPI: 'X', ACTIVCO: 'X' },
  ];

  async calculate(context: Record<string, any>): Promise<LocalTaxResult | null> {
    const sanitizedContext = this.sanitizeContext(context);

    if (this.toNumber(sanitizedContext.price) <= 0) {
      console.warn('[TAX] price <= 0, abortando cálculo.');
      return null;
    }

    const taxesMap = await this.getTaxesMap(sanitizedContext);

    if (taxesMap.size === 0) {
      return null;
    }

    const conditions: LocalTaxCondition[] = [];

    const ipiCondition = this.calculateIPI(sanitizedContext, taxesMap);
    if (ipiCondition) {
      conditions.push(ipiCondition);
    }

    conditions.push(...this.calculateICMS(sanitizedContext, taxesMap));
    conditions.push(...this.calculateDifal(sanitizedContext, taxesMap));

    const pisCondition = this.calculatePIS(sanitizedContext, taxesMap);
    if (pisCondition) {
      conditions.push(pisCondition);
    }

    const cofinsCondition = this.calculateCOFINS(sanitizedContext, taxesMap);
    if (cofinsCondition) {
      conditions.push(cofinsCondition);
    }

    conditions.push(...this.calculateST(sanitizedContext, taxesMap, conditions));

    return { conditions };
  }

  private async getTaxesMap(context: TaxContext): Promise<Map<string, TaxTableBase>> {
    const [icms1, icms3, ipi1, ipi3, st3, pis, cofins] = await Promise.all([
      firstValueFrom(this.icms1Repo.getAll()),
      firstValueFrom(this.icms3Repo.getAll()),
      firstValueFrom(this.ipi1Repo.getAll()),
      firstValueFrom(this.ipi3Repo.getAll()),
      firstValueFrom(this.st3Repo.getAll()),
      firstValueFrom(this.pisRepo.getAll()),
      firstValueFrom(this.cofinsRepo.getAll()),
    ]);

    const taxes = new Map<string, TaxTableBase>();
    const icms = this.findICMS(context, icms1, icms3, false);
    const internalIcms = this.findICMS(context, icms1, icms3, true);
    const ipi = this.findIPI(context, ipi1, ipi3);
    const subtrib = this.findST(context, st3);
    const pisTax = this.findPIS(context, pis);
    const cofinsTax = this.findCOFINS(context, cofins);

    if (icms) taxes.set('ICMS', icms);
    if (internalIcms) taxes.set('INTERNAL_ICMS', internalIcms);
    if (ipi) taxes.set('IPI', ipi);
    if (subtrib) taxes.set('SUBTRIB', subtrib);
    if (pisTax) taxes.set('PIS', pisTax);
    if (cofinsTax) taxes.set('COFINS', cofinsTax);

    return taxes;
  }

  private findICMS(
    context: TaxContext,
    directRows: ICMS1TaxTable[],
    dynamicRows: ICMS3TaxTable[],
    internal: boolean,
  ): ICMS1TaxTable | ICMS3TaxTable | undefined {
    const shipFrom = internal ? context.SHIPTO : context.SHIPFROM;
    const direct = this.pickLatest(
      directRows.filter(
        row =>
          this.matchesValue(row.LAND1, context.LAND1) &&
          this.matchesValue(row.SHIPFROM, shipFrom) &&
          this.matchesValue(row.SHIPTO, context.SHIPTO) &&
          this.isWithinRange(row, context.VALIDFROM, context.VALIDTO),
      ),
    );

    if (direct) {
      return direct;
    }

    return this.findDynamicTax(
      'ICMS',
      dynamicRows,
      context,
      row =>
        this.matchesValue(row.LAND1, context.LAND1) &&
        this.matchesValue(row.SHIPFROM, shipFrom) &&
        this.matchesValue(row.SHIPTO, context.SHIPTO) &&
        this.isWithinRange(row, context.VALIDFROM, context.VALIDTO),
    );
  }

  private findIPI(
    context: TaxContext,
    directRows: IPI1TaxTable[],
    dynamicRows: IPI3TaxTable[],
  ): IPI1TaxTable | IPI3TaxTable | undefined {
    const direct = this.pickLatest(
      directRows.filter(
        row =>
          this.matchesValue(row.NBMCODE, context.NBMCODE) &&
          this.isWithinRange(row, context.VALIDFROM, context.VALIDTO),
      ),
    );

    if (direct) {
      return direct;
    }

    return this.findDynamicTax('IPI', dynamicRows, context, row =>
      this.isWithinRange(row, context.VALIDFROM, context.VALIDTO),
    );
  }

  private findST(context: TaxContext, rows: ST3TaxTable[]): ST3TaxTable | undefined {
    return this.findDynamicTax(
      'ST',
      rows,
      context,
      row =>
        this.matchesValue(row.LAND1, context.LAND1) &&
        this.matchesValue(row.SHIPFROM, context.SHIPFROM) &&
        this.matchesValue(row.SHIPTO, context.SHIPTO) &&
        this.matchesOptionalValue(row.STGRP, context.STGRP) &&
        this.isWithinRange(row, context.VALIDFROM, context.VALIDTO),
    );
  }

  private findPIS(context: TaxContext, rows: PISTaxTable[]): PISTaxTable | undefined {
    return this.findDynamicTax(
      'PIS',
      rows,
      context,
      row =>
        this.matchesValue(row.COUNTRY, context.COUNTRY) && this.isWithinRange(row, context.VALIDFROM, context.VALIDTO),
    );
  }

  private findCOFINS(context: TaxContext, rows: COFINSTaxTable[]): COFINSTaxTable | undefined {
    return this.findDynamicTax(
      'COFINS',
      rows,
      context,
      row =>
        this.matchesValue(row.COUNTRY, context.COUNTRY) && this.isWithinRange(row, context.VALIDFROM, context.VALIDTO),
    );
  }

  private findDynamicTax<
    T extends TaxTableBase & {
      GRUOP?: number | string | null;
      VALUE?: string | null;
      VALUE2?: string | null;
      VALUE3?: string | null;
    },
  >(taxKey: DynamicTaxKey, rows: T[], context: TaxContext, baseFilter: (row: T) => boolean): T | undefined {
    for (const group of this.getDynamicGroupsForTax(taxKey)) {
      const matches = rows.filter(
        row =>
          this.toNumber(row.GRUOP) === group.GRUOP &&
          baseFilter(row) &&
          this.matchesDynamicField(row.VALUE, group, 'FIELD', context) &&
          this.matchesDynamicField(row.VALUE2, group, 'FIELD2', context) &&
          this.matchesDynamicField(row.VALUE3, group, 'FIELD3', context),
      );

      const result = this.pickLatest(matches);
      if (result) {
        return result;
      }
    }

    return undefined;
  }

  private calculateIPI(context: TaxContext, taxesMap: Map<string, TaxTableBase>): LocalTaxCondition | null {
    const ipiConfig = taxesMap.get('IPI');
    const baseValue = this.toNumber(context.price);
    if (!ipiConfig) {
      return this.buildCondition('BX23', 0, 0, 0, 0, 'Valor IPI');
    }

    const base = this.toNumber(ipiConfig.BASE, 100);
    const rate = this.toNumber(ipiConfig.RATE) / 100;
    const ipiBase = (baseValue * base) / 100;
    return this.buildCondition('BX23', base, baseValue, rate, ipiBase * rate, 'Valor IPI');
  }

  private calculateICMS(context: TaxContext, taxesMap: Map<string, TaxTableBase>): LocalTaxCondition[] {
    if (context.ICMSTAXPAY !== '00') {
      return [
        this.buildCondition('BX13', 0, 0, 0, 0, 'Valor ICMS'),
        this.buildCondition('BX44', 0, 0, 0, 0, 'Valor FCP'),
      ];
    }

    const icmsConfig = taxesMap.get('ICMS') as ICMS1TaxTable | ICMS3TaxTable | undefined;
    const baseValue = this.toNumber(context.price);
    if (!icmsConfig) {
      return [
        this.buildCondition('BX13', 0, 0, 0, 0, 'Valor ICMS'),
        this.buildCondition('BX44', 0, 0, 0, 0, 'Valor FCP'),
      ];
    }

    const icmsBase = this.toNumber(icmsConfig.BASE, 100);
    const icmsRate = this.toNumber(icmsConfig.RATE) / 100;
    const fcpBase = this.toNumber(icmsConfig.SPECF_BASE, 100);
    const fcpRate = this.toNumber(icmsConfig.SPECF_RATE) / 100;
    const route = `${icmsConfig.SHIPFROM ?? context.SHIPFROM} > ${icmsConfig.SHIPTO ?? context.SHIPTO}`;

    return [
      this.buildCondition(
        'BX13',
        icmsBase,
        baseValue,
        icmsRate,
        ((baseValue * icmsBase) / 100) * icmsRate,
        `Valor ICMS ( ${route})`,
      ),
      this.buildCondition(
        'BX44',
        fcpBase,
        baseValue,
        fcpRate,
        ((baseValue * fcpBase) / 100) * fcpRate,
        `FCP ( ${route})`,
      ),
    ];
  }

  private calculateDifal(context: TaxContext, taxesMap: Map<string, TaxTableBase>): LocalTaxCondition[] {
    const icmsConfig = taxesMap.get('ICMS') as ICMS1TaxTable | ICMS3TaxTable | undefined;
    const internalIcmsConfig = taxesMap.get('INTERNAL_ICMS') as ICMS1TaxTable | ICMS3TaxTable | undefined;
    const baseValue = this.toNumber(context.price);

    if (!icmsConfig || !internalIcmsConfig) {
      return [
        this.buildCondition('BX45', 0, 0, 0, 0, 'ICMS Origem x Destino'),
        this.buildCondition('BX46', 0, 0, 0, 0, 'ICMS Destino X Destino'),
        this.buildCondition('DIFAL', 0, 0, 0, 0, 'DIFAL'),
        this.buildCondition('FCP_DIFAL', 0, 0, 0, 0, 'FCP DIFAL'),
      ];
    }

    const originRatePercent = this.toNumber(icmsConfig.RATE) - this.toNumber(icmsConfig.SPECF_RATE);
    const internalFcpRatePercent = this.toNumber(internalIcmsConfig.SPECF_RATE);
    const internalRatePercent = this.toNumber(internalIcmsConfig.RATE) - internalFcpRatePercent;
    const originValue = (baseValue * originRatePercent) / 100;
    const internalValue = (baseValue * internalRatePercent) / 100;
    const difalRatePercent = internalRatePercent - originRatePercent;
    // FCP_DIFAL: only the destination FCP applies on interstate operations (origin FCP = 0).
    // Using internalFcpRatePercent directly avoids a negative rate when origin ICMS > internal FCP.
    const difalFcpRatePercent = internalFcpRatePercent;

    return [
      this.buildCondition('BX45', 100, baseValue, originRatePercent / 100, originValue, 'ICMS Origem x Destino'),
      this.buildCondition('BX46', 100, baseValue, internalRatePercent / 100, internalValue, 'ICMS Destino X Destino'),
      this.buildCondition('DIFAL', 100, baseValue, difalRatePercent / 100, internalValue - originValue, 'DIFAL'),
      this.buildCondition(
        'FCP_DIFAL',
        100,
        baseValue,
        difalFcpRatePercent / 100,
        (baseValue * difalFcpRatePercent) / 100,
        'FCP DIFAL',
      ),
    ];
  }

  private calculatePIS(context: TaxContext, taxesMap: Map<string, TaxTableBase>): LocalTaxCondition | null {
    const pisConfig = taxesMap.get('PIS') as PISTaxTable | undefined;
    const cofinsConfig = taxesMap.get('COFINS') as COFINSTaxTable | undefined;
    const icmsConfig = taxesMap.get('ICMS') as ICMS1TaxTable | ICMS3TaxTable | undefined;
    const baseValue = this.toNumber(context.price);

    if (!pisConfig || !cofinsConfig) {
      return this.buildCondition('BX51', 0, 0, 0, 0, 'Valor PIS');
    }

    const taxableBase = baseValue - this.calculateIcmsValueForContribution(baseValue, icmsConfig);
    const base = this.toNumber(pisConfig.BASE, 100);
    const rate = this.toNumber(pisConfig.RATE) / 100;
    return this.buildCondition('BX51', base, baseValue, rate, ((taxableBase * base) / 100) * rate, 'Valor PIS');
  }

  private calculateCOFINS(context: TaxContext, taxesMap: Map<string, TaxTableBase>): LocalTaxCondition | null {
    const cofinsConfig = taxesMap.get('COFINS') as COFINSTaxTable | undefined;
    const pisConfig = taxesMap.get('PIS') as PISTaxTable | undefined;
    const icmsConfig = taxesMap.get('ICMS') as ICMS1TaxTable | ICMS3TaxTable | undefined;
    const baseValue = this.toNumber(context.price);

    if (!cofinsConfig || !pisConfig) {
      return this.buildCondition('BX52', 0, 0, 0, 0, 'Valor COFINS');
    }

    const taxableBase = baseValue - this.calculateIcmsValueForContribution(baseValue, icmsConfig);
    const base = this.toNumber(cofinsConfig.BASE, 100);
    const rate = this.toNumber(cofinsConfig.RATE) / 100;
    return this.buildCondition('BX52', base, baseValue, rate, ((taxableBase * base) / 100) * rate, 'Valor COFINS');
  }

  private calculateST(
    context: TaxContext,
    taxesMap: Map<string, TaxTableBase>,
    conditions: LocalTaxCondition[],
  ): LocalTaxCondition[] {
    if (context.STGRP !== '01') {
      return [
        this.buildCondition('FCP_ST', 0, 0, 0, 0, 'Valor FCP ST'),
        this.buildCondition('BX41', 0, 0, 0, 0, 'Valor ICMS ST'),
      ];
    }

    const internalIcmsConfig = taxesMap.get('INTERNAL_ICMS') as ICMS1TaxTable | ICMS3TaxTable | undefined;
    const stConfig = taxesMap.get('SUBTRIB') as ST3TaxTable | undefined;
    if (!internalIcmsConfig || !stConfig) {
      return [
        this.buildCondition('FCP_ST', 0, 0, 0, 0, 'Valor FCP ST'),
        this.buildCondition('BX41', 0, 0, 0, 0, 'Valor ICMS ST'),
      ];
    }

    const baseValue = this.toNumber(context.price);
    const ipiValue = conditions.find(condition => condition.condition === 'BX23')?.value ?? 0;
    const icmsValue = conditions.find(condition => condition.condition === 'BX13')?.value ?? 0;
    const stRate = this.toNumber(stConfig.RATE) / 100;
    const stBase = (1 + stRate) * (baseValue + ipiValue);
    const internalRate = this.toNumber(internalIcmsConfig.RATE) / 100;
    const stValue = stBase * internalRate - icmsValue;

    return [
      this.buildCondition('FCP_ST', 100, baseValue, 0, 0, 'Valor FCP ST'),
      this.buildCondition('BX41', 100, stBase, stRate, stValue, 'Valor ICMS ST'),
    ];
  }

  private calculateIcmsValueForContribution(baseValue: number, icmsConfig?: ICMS1TaxTable | ICMS3TaxTable): number {
    if (!icmsConfig) {
      return 0;
    }

    const rate = this.toNumber(icmsConfig.RATE) / 100;
    const base = this.toNumber(icmsConfig.BASE, 100);
    return ((baseValue * base) / 100) * rate;
  }

  private sanitizeContext(context: Record<string, any>): TaxContext {
    const validFrom = this.toDate(context['VALIDFROM'] ?? context['DATAB'] ?? new Date());
    validFrom.setUTCHours(0, 0, 0, 0);
    const validTo = this.toDate(context['VALIDTO'] ?? context['DATBI'] ?? validFrom);
    validTo.setUTCHours(0, 0, 0, 0);

    return {
      ...context,
      price: this.toNumber(context['price']),
      VALIDFROM: validFrom,
      VALIDTO: validTo,
      COUNTRY: this.normalizeString(context['COUNTRY'] ?? context['LAND1']),
      LAND1: this.normalizeString(context['LAND1'] ?? context['COUNTRY']),
      SHIPFROM: this.resolveContextValue(context['SHIPFROM']),
      SHIPTO: this.resolveContextValue(context['SHIPTO']),
      STGRP: this.normalizeString(context['STGRP']),
      ICMSTAXPAY: this.resolveContextValue(context['ICMSTAXPAY']),
      BRSCH: this.normalizeString(context['BRSCH']),
      MATNR: this.resolveContextValue(context['MATNR']),
      NBM: this.resolveContextValue(context['NBM']),
      NBMCODE: this.resolveContextValue(context['NBMCODE']),
      EXTWG: this.resolveContextValue(context['EXTWG']),
      MTORG: this.resolveContextValue(context['MTORG']),
      // WERKS can be a {in:[...]} candidate value — resolve to the first candidate
      WERKS: this.resolveContextValue(context['WERKS']),
      MWSKZ: this.resolveContextValue(context['MWSKZ']),
      KUNNR: this.resolveContextValue(context['KUNNR']),
    };
  }

  private getDynamicGroupsForTax(taxKey: DynamicTaxKey): DynamicTaxGroup[] {
    const flags: Record<DynamicTaxKey, keyof DynamicTaxGroup> = {
      ICMS: 'ACTIVIC',
      IPI: 'ACTIVIP',
      ST: 'ACTIVST',
      PIS: 'ACTIVPI',
      COFINS: 'ACTIVCO',
    };

    return this.dynamicGroups.filter(group => group[flags[taxKey]] === 'X');
  }

  private matchesDynamicField(
    rowValue: string | null | undefined,
    group: DynamicTaxGroup,
    fieldName: DynamicFieldName,
    context: TaxContext,
  ): boolean {
    const contextField = group[fieldName];
    if (!contextField) {
      return this.matchesOptionalValue(rowValue, null);
    }

    return this.matchesOptionalValue(
      rowValue,
      this.resolveContextValue((context as unknown as Record<string, unknown>)[contextField]),
    );
  }

  private resolveContextValue(value: unknown): string | null {
    if (value && typeof value === 'object' && 'in' in (value as Record<string, unknown>)) {
      const candidates = (value as { in?: unknown[] }).in;
      return Array.isArray(candidates) ? this.normalizeString(candidates[0]) : null;
    }

    return this.normalizeString(value);
  }

  private isWithinRange(row: TaxTableBase, validFrom: Date, _validTo: Date): boolean {
    const rowValidFrom = this.toDate(row.VALIDFROM);
    return rowValidFrom.getTime() <= validFrom.getTime();
  }

  private pickLatest<T extends TaxTableBase>(rows: T[]): T | undefined {
    if (!rows.length) {
      return undefined;
    }

    return [...rows].sort(
      (left, right) => this.toDate(right.VALIDFROM).getTime() - this.toDate(left.VALIDFROM).getTime(),
    )[0];
  }

  private matchesValue(actual: unknown, expected: unknown): boolean {
    return this.normalizeString(actual) === this.normalizeString(expected);
  }

  private matchesOptionalValue(actual: unknown, expected: unknown): boolean {
    return (this.normalizeString(actual) ?? null) === (this.normalizeString(expected) ?? null);
  }

  private buildCondition(
    condition: string,
    base: number,
    baseValue: number,
    rate: number,
    value: number,
    description: string,
  ): LocalTaxCondition {
    return {
      condition,
      base: this.round(base),
      baseValue: this.round(baseValue),
      rate: this.round(rate, 5),
      value: this.round(value, 5),
      description,
    };
  }

  private normalizeString(value: unknown): string | null {
    const normalized = String(value ?? '').trim();
    return normalized ? normalized : null;
  }

  private toNumber(value: unknown, fallback = 0): number {
    const parsed = Number(value ?? fallback);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private toDate(value: unknown): Date {
    const date =
      value instanceof Date ? new Date(value) : new Date((value as string | number | Date | undefined) ?? new Date());
    return Number.isNaN(date.getTime()) ? new Date() : date;
  }

  private round(value: number, digits = 2): number {
    return Number.parseFloat(Number(value || 0).toFixed(digits));
  }
}
